import { useEffect } from 'react';
import {
  useCurrentThreadId,
  useGetUser,
  useRightSidePanelActions,
  useRightSidePanelState,
  useInsertLoadedContentToEditor,
  useQueryParams,
  useErrorToast,
} from 'src/hooks';
import { RightPanelSections } from 'src/types';
import { useLazyGetEditorThreadByIdQuery } from 'src/store/services';

// The hook must be called only once in the Right Panel
export const useLoadDefaultEditorContent = () => {
  const { removeSearchParam } = useQueryParams();
  const { currentThreadId } = useCurrentThreadId();

  const { user_id } = useGetUser();
  const { isEditorPanelExpanded } = useRightSidePanelState();
  const {
    setRightSidePanelData,
    toggleRightSidePanelFullWidth,
    toggleRightSidePanelExpanded,
  } = useRightSidePanelActions();

  const [trigger] = useLazyGetEditorThreadByIdQuery();
  const { onInsertLoadedContent } = useInsertLoadedContentToEditor();

  const { onShowErrorToast } = useErrorToast();

  useEffect(() => {
    const loadEditorData = async () => {
      if (!currentThreadId) {
        return;
      }

      try {
        const thread = await trigger(
          { user_id, thread_id: currentThreadId },
          true,
        ).unwrap();

        if (!thread) {
          return;
        }

        if (Object.keys(thread).length === 0) {
          // Handling a case if a user comes back to a deleted document
          removeSearchParam('editor', { replace: true });
          return;
        }

        onInsertLoadedContent(thread);

        setRightSidePanelData({
          panelType: RightPanelSections.EDITOR,
          isExpanded: true,
          editorType: thread.thread_type,
        });
      } catch (error) {
        removeSearchParam('editor', { replace: true });
        onShowErrorToast('Document not found');
      }
    };

    if (currentThreadId) {
      loadEditorData();
      return;
    }
    if (!currentThreadId && isEditorPanelExpanded) {
      toggleRightSidePanelFullWidth(false);
      toggleRightSidePanelExpanded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThreadId]);
};
